import React, { Fragment, useState } from "react";
import { Link } from "react-router";
import { Popover } from "@ark-ui/react";
import {
  ArrowRightStartOnRectangleIcon,
  CameraIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  GiftIcon,
  GlobeAltIcon,
  SparklesIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../../hooks/useAuth";
import { useUserStore } from "../../../store/userStore";
import { useCommonStore } from "../../../store/commonStore";
import Button from "../../atoms/Button";
import InboxNotifications from "../../molecules/notifications/InboxNotifications";
import GeneralNotifications from "../../molecules/notifications/GeneralNotifications";

function HeaderDesktop({ routes }: { routes: any[] }) {
  const { t } = useTranslation(["common"]);
  const user = useUserStore((state) => state.user);
  const { logout } = useAuth();

  const setDisplayInbox = useCommonStore((state) => state.setDisplayInbox);
  const setDisplayNotifications = useCommonStore(
    (state) => state.setDisplayNotifications
  );

  const [displayExchanges, setDisplayExchanges] = useState(false);
  const [displayGallery, setDisplayGallery] = useState(false);
  const [displayDiscover, setDisplayDiscover] = useState(false);
  const [displayPremium, setDisplayPremium] = useState(false);
  const [displayAdmin, setDisplayAdmin] = useState(false);

  return (
    <>
      <header className={`bg-surface-base max-[1023px]:hidden text-text-base`}>
        <div className="p-3 lg:py-5 lg:container lg:mx-auto flex items-center gap-10">
          <Link
            to="#main-content"
            className="block sr-only focus:not-sr-only bg-surface-base !px-2 !py-1 rounded-2xl !absolute top-2 left-2 focus:ring focus:ring-blue-500"
            onClick={() => {
              document.getElementById("main-content")?.focus();
            }}
          >
            Skip to main content
          </Link>
          <div>
            <Link to="/" className="flex flex-row items-center gap-3">
              <img
                src={`/assets/mobileLogo.svg`}
                alt="Givin Gifts"
                width={48}
                height={48}
              />
            </Link>
          </div>
          <nav className="ml-auto mr-0">
            <ul className="flex flex-row gap-3 xl:gap-5 text-text-primary text-end">
              {routes.map((route) => (
                <Fragment key={route.title}>
                  {(route.auth && !user) ||
                  (route.admin &&
                    (!user.roles || !user.roles.includes("admin"))) ? (
                    <></>
                  ) : (
                    <li>
                      <Popover.Root
                        open={
                          route.title === "exchanges"
                            ? displayExchanges
                            : route.title === "gallery"
                            ? displayGallery
                            : route.title === "discover"
                            ? displayDiscover
                            : route.title === "premium"
                            ? displayPremium
                            : route.title === "admin"
                            ? displayAdmin
                            : false
                        }
                        onOpenChange={(open) => {
                          if (route.title === "exchanges") {
                            setDisplayExchanges(open.open);
                            setDisplayAdmin(false);
                            setDisplayDiscover(false);
                            setDisplayGallery(false);
                            setDisplayPremium(false);
                            setDisplayInbox(false);
                            setDisplayNotifications(false);
                          }

                          if (route.title === "gallery") {
                            setDisplayGallery(open.open);
                            setDisplayAdmin(false);
                            setDisplayDiscover(false);
                            setDisplayExchanges(false);
                            setDisplayPremium(false);
                            setDisplayInbox(false);
                            setDisplayNotifications(false);
                          }

                          if (route.title === "discover") {
                            setDisplayDiscover(open.open);
                            setDisplayAdmin(false);
                            setDisplayExchanges(false);
                            setDisplayGallery(false);
                            setDisplayPremium(false);
                            setDisplayInbox(false);
                            setDisplayNotifications(false);
                          }

                          if (route.title === "premium") {
                            setDisplayPremium(open.open);
                            setDisplayAdmin(false);
                            setDisplayExchanges(false);
                            setDisplayGallery(false);
                            setDisplayDiscover(false);
                            setDisplayInbox(false);
                            setDisplayNotifications(false);
                          }

                          if (route.title === "admin") {
                            setDisplayAdmin(open.open);
                            setDisplayExchanges(false);
                            setDisplayGallery(false);
                            setDisplayDiscover(false);
                            setDisplayPremium(false);
                            setDisplayInbox(false);
                            setDisplayNotifications(false);
                          }
                        }}
                      >
                        <Popover.Trigger className="hover:cursor-pointer flex flex-row items-center rounded-full hover:bg-surface-base active:bg-surface-base hover:shadow-md active:shadow-lg hover:bg-gradient-to-b hover:from-[#F0337233] hover:to-[#404A9022] active:bg-gradient-to-b active:from-[#F0337255] active:to-[#404A9044] py-2 px-4">
                          <span className="text-text-primary">
                            {t(`header.topLevel.${route.title}`)}
                          </span>
                          <span className="text-text-base ml-2">
                            <ChevronDownIcon className="w-5 h-5" />
                          </span>
                        </Popover.Trigger>
                        <Popover.Positioner>
                          <Popover.Content className="bg-surface-tertiary rounded-2xl shadow-xl p-3 pb-3 z-50 min-w-0 max-w-sm">
                            {route.pages.map((page: any) => (
                              <Fragment key={page.title}>
                                {(page.auth && !user) ||
                                (page.admin &&
                                  user &&
                                  !user.roles.includes("admin")) ||
                                (page.premium &&
                                  user &&
                                  user.roles &&
                                  !user.roles.includes("premium")) ? (
                                  <></>
                                ) : (
                                  <Link
                                    to={page.href}
                                    className="mb-3 last-of-type:mb-0 flex w-full"
                                    onClick={() => {
                                      setDisplayAdmin(false);
                                      setDisplayExchanges(false);
                                      setDisplayGallery(false);
                                      setDisplayDiscover(false);
                                      setDisplayPremium(false);
                                      setDisplayInbox(false);
                                      setDisplayNotifications(false);
                                    }}
                                  >
                                    <div className="flex flex-row items-center bg-surface-base hover:bg-surface-lowered text-text-base rounded-xl p-3 z-50 group w-full">
                                      {page.icon ? (
                                        <div className="mr-5 text-text-secondary">
                                          {page.icon}
                                        </div>
                                      ) : (
                                        ""
                                      )}
                                      <div className="mr-3 text-start">
                                        <div className="font-bold text-text-primary">
                                          {page.title}
                                        </div>
                                        {page.description ? (
                                          <div className="text-start text-sm">
                                            {page.description}
                                          </div>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="ml-auto mr-0 text-end">
                                        <ChevronRightIcon className="w-4 h-4" />
                                      </div>
                                    </div>
                                  </Link>
                                )}
                              </Fragment>
                            ))}
                          </Popover.Content>
                        </Popover.Positioner>
                      </Popover.Root>
                    </li>
                  )}
                </Fragment>
              ))}
            </ul>
          </nav>
          {user ? (
            <div className="flex gap-5">
              <>
                <InboxNotifications />
                <GeneralNotifications />
                <Popover.Root>
                  <Popover.Trigger
                    className="hover:cursor-pointer bg-surface-base hover:bg-[#E5E5E5] active:bg-[#CCCCCC] text-text-base rounded-full shadow-sm hover:shadow-md lg:text-lg flex bg-surface-base shadow-md hover:shadow-lg rounded-xl relative h-[2.875rem] w-[2.875rem]"
                    onClick={() => {
                      setDisplayInbox(false);
                      setDisplayNotifications(false);
                    }}
                  >
                    <img
                      src={`https://images.givin.gifts/preset:sharp/resize:fit:720:0:0/format:webp/gravity:sm/plain/https://storage.ethereal.sh/gifts-public/${user.picture}`}
                      alt="Avatar"
                      className="w-full h-full rounded-xl aspect-square object-cover"
                    />
                  </Popover.Trigger>
                  <Popover.Positioner>
                    <Popover.Content className="bg-surface-raised rounded-2xl p-3 pb-1 text-text-base max-h-[80vh] overflow-y-auto z-50 shadow-xl min-w-0 max-w-sm">
                      <Link
                        to={`/account`}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setDisplayAdmin(false);
                          setDisplayExchanges(false);
                          setDisplayGallery(false);
                          setDisplayDiscover(false);
                          setDisplayPremium(false);
                          setDisplayInbox(false);
                          setDisplayNotifications(false);
                        }}
                      >
                        <div className="flex flex-row items-center mb-3 bg-surface-base hover:bg-surface-lowered rounded-xl p-3 z-50 group">
                          <div className="mr-5 text-text-base text-text-secondary">
                            <UserCircleIcon className="w-5 h-5" />
                          </div>
                          <div className="mr-3 text-start">
                            <div className="font-bold text-text-primary">
                              My account
                            </div>
                            <div className="text-start text-sm text-text-base">
                              View and update your accounts details.
                            </div>
                          </div>
                          <div className="ml-auto mr-0 text-end text-text-base">
                            <ChevronRightIcon className="w-4 h-4" />
                          </div>
                        </div>
                      </Link>
                      {user && user.nickname && user.nickname !== "" && (
                        <Link
                          to={`/profile/${user.nickname}`}
                          className="hover:cursor-pointer"
                          onClick={() => {
                            setDisplayAdmin(false);
                            setDisplayExchanges(false);
                            setDisplayGallery(false);
                            setDisplayDiscover(false);
                            setDisplayPremium(false);
                            setDisplayInbox(false);
                            setDisplayNotifications(false);
                          }}
                        >
                          <div className="flex flex-row items-center mb-3 bg-surface-base hover:bg-surface-lowered rounded-xl p-3 z-50 group">
                            <div className="mr-5 text-text-base text-text-secondary">
                              <GlobeAltIcon className="w-5 h-5" />
                            </div>
                            <div className="mr-3 text-start">
                              <div className="font-bold text-text-primary">
                                My profile
                              </div>
                              <div className="text-start text-sm text-text-base">
                                View your public profile.
                              </div>
                            </div>
                            <div className="ml-auto mr-0 text-end text-text-base">
                              <ChevronRightIcon className="w-4 h-4" />
                            </div>
                          </div>
                        </Link>
                      )}
                      <Link
                        to={`/exchanges/my-exchanges`}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setDisplayAdmin(false);
                          setDisplayExchanges(false);
                          setDisplayGallery(false);
                          setDisplayDiscover(false);
                          setDisplayPremium(false);
                          setDisplayInbox(false);
                          setDisplayNotifications(false);
                        }}
                      >
                        <div className="flex flex-row items-center mb-3 bg-surface-base hover:bg-surface-lowered rounded-xl p-3 z-50 group">
                          <div className="mr-5 text-text-base text-text-secondary">
                            <GiftIcon className="w-5 h-5" />
                          </div>
                          <div className="mr-3 text-start">
                            <div className="font-bold text-text-primary">
                              My exchanges
                            </div>
                            <div className="text-start text-sm text-text-base">
                              View your active and historical exchanges.
                            </div>
                          </div>
                          <div className="ml-auto mr-0 text-end text-text-base">
                            <ChevronRightIcon className="w-4 h-4" />
                          </div>
                        </div>
                      </Link>
                      <Link
                        to={`/gallery/my-posts`}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setDisplayAdmin(false);
                          setDisplayExchanges(false);
                          setDisplayGallery(false);
                          setDisplayDiscover(false);
                          setDisplayPremium(false);
                          setDisplayInbox(false);
                          setDisplayNotifications(false);
                        }}
                      >
                        <div className="flex flex-row items-center mb-3 bg-surface-base hover:bg-surface-lowered rounded-xl p-3 z-50 group">
                          <div className="mr-5 text-text-base text-text-secondary">
                            <CameraIcon className="w-5 h-5" />
                          </div>
                          <div className="mr-3 text-start">
                            <div className="font-bold text-text-primary">
                              My posts
                            </div>
                            <div className="text-start text-sm text-text-base">
                              View the gifts you have sent and received.
                            </div>
                          </div>
                          <div className="ml-auto mr-0 text-end text-text-base">
                            <ChevronRightIcon className="w-4 h-4" />
                          </div>
                        </div>
                      </Link>
                      <Link
                        to={`/exchanges/my-rematches`}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setDisplayAdmin(false);
                          setDisplayExchanges(false);
                          setDisplayGallery(false);
                          setDisplayDiscover(false);
                          setDisplayPremium(false);
                          setDisplayInbox(false);
                          setDisplayNotifications(false);
                        }}
                      >
                        <div className="flex flex-row items-center mb-3 bg-surface-base hover:bg-surface-lowered rounded-xl p-3 z-50 group">
                          <div className="mr-5 text-text-base text-text-secondary">
                            <SparklesIcon className="w-5 h-5" />
                          </div>
                          <div className="mr-3 text-start">
                            <div className="font-bold text-text-primary">
                              My rematches
                            </div>
                            <div className="text-start text-sm text-text-base">
                              View your active and historical rematches.
                            </div>
                          </div>
                          <div className="ml-auto mr-0 text-end text-text-base">
                            <ChevronRightIcon className="w-4 h-4" />
                          </div>
                        </div>
                      </Link>
                      <button
                        className="w-full hover:cursor-pointer"
                        onClick={() => {
                          setDisplayInbox(false);
                          setDisplayNotifications(false);
                          logout();
                        }}
                      >
                        <div className="flex flex-row items-center mb-3 bg-surface-base hover:bg-surface-lowered rounded-xl p-3 z-50 group">
                          <div className="mr-5 text-text-base text-text-secondary">
                            <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
                          </div>
                          <div className="mr-3 text-start">
                            <div className="font-bold text-text-primary">
                              Log out
                            </div>
                            <div className="text-start text-sm text-text-base">
                              Log out of Givin Gifts, until next time!
                            </div>
                          </div>
                          <div className="ml-auto mr-0 text-end text-text-base">
                            <ChevronRightIcon className="w-4 h-4" />
                          </div>
                        </div>
                      </button>
                    </Popover.Content>
                  </Popover.Positioner>
                </Popover.Root>
              </>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                link="/register"
                style="secondary"
                className="hover:cursor-pointer"
              >
                {t("header.buttons.register")}
              </Button>
              <Button
                link="/login"
                style="tertiary"
                className="hover:cursor-pointer"
              >
                {t("header.buttons.login")}
              </Button>
            </div>
          )}
        </div>
      </header>
    </>
  );
}

export default HeaderDesktop;
